import { useState, useMemo, useEffect } from "react";
import { convertToCSV, downloadCSV, generateFilename } from "@/lib/csvUtils";

export type ExportScope = "filtered" | "searched" | "page" | "all" | "selected";

export interface CSVDownloadOptions {
  data: any[];
  filteredData: any[];
  searchedData: any[];
  currentPageData: any[];
  selectedData: any[];
  allColumns: string[];
  headerFunc?: (key: string) => string;
  override?: Record<string, string>;
  baseFilename?: string;
}

export function useCSVDownload(options: CSVDownloadOptions) {
  const {
    data,
    filteredData,
    searchedData,
    currentPageData,
    selectedData,
    allColumns,
    headerFunc,
    override,
    baseFilename = "table_export",
  } = options;

  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set(allColumns));
  const [scope, setScope] = useState<ExportScope>("all");
  const [isGenerating, setIsGenerating] = useState(false);

  // Fall back to "all" if scope is "selected" but no rows are selected
  useEffect(() => {
    if (scope === "selected" && selectedData.length === 0) {
      setScope("all");
    }
  }, [scope, selectedData.length]);

  // Get the data to export based on scope
  const dataToExport = useMemo(() => {
    switch (scope) {
      case "filtered":
        return filteredData;
      case "searched":
        return searchedData;
      case "page":
        return currentPageData;
      case "all":
        return data;
      case "selected":
        return selectedData;
      default:
        return data;
    }
  }, [scope, data, filteredData, searchedData, currentPageData, selectedData]);

  // Get the columns to export as array
  const columnsToExport = useMemo(() => {
    return allColumns.filter((column) => selectedColumns.has(column));
  }, [allColumns, selectedColumns]);

  // Toggle column selection
  const toggleColumn = (columnKey: string) => {
    setSelectedColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  // Toggle all columns
  const toggleAllColumns = () => {
    if (selectedColumns.size === allColumns.length) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(allColumns));
    }
  };

  // Generate and download CSV
  const downloadCSVFile = async () => {
    if (columnsToExport.length === 0) {
      throw new Error("Please select at least one column to export");
    }

    setIsGenerating(true);

    try {
      const csvContent = convertToCSV(dataToExport, columnsToExport, headerFunc, override);

      const filename = generateFilename(baseFilename);
      downloadCSV(csvContent, filename);

      return { success: true, filename };
    } catch (error) {
      console.error("Error generating CSV:", error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  // Get summary info
  const summary = useMemo(() => {
    return {
      selectedColumnsCount: selectedColumns.size,
      totalColumnsCount: allColumns.length,
      rowsToExport: dataToExport.length,
      filteredCount: filteredData.length,
      searchedCount: searchedData.length,
      pageCount: currentPageData.length,
      allCount: data.length,
      selectedCount: selectedData.length,
      scope,
    };
  }, [
    selectedColumns.size,
    allColumns.length,
    dataToExport.length,
    filteredData.length,
    searchedData.length,
    currentPageData.length,
    data.length,
    selectedData.length,
    scope,
  ]);

  return {
    selectedColumns,
    scope,
    isGenerating,
    columnsToExport,
    summary,
    toggleColumn,
    toggleAllColumns,
    setScope,
    downloadCSVFile,
  };
}
